import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../api/client'
import type { SoftwareDeadline } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { useAuth } from '../auth/AuthContext'
import { livelloModulo } from '../auth/permessi'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { ModuloEstensione, dataBreve, type Partenza } from '../components/EstensioniSupporto'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Inventario Software → Scadenze dei software: the end of support of the
 * installed software and of the operating systems, as the assessment judges
 * it (catalogues from endoflife.date): already out of support, behind
 * (rolling products), and leaving support within six months. Each row is a
 * finding of the CI's Security section. See software_inventory_service.
 * A contract's extended support turns a row into «supporto esteso fino al …»;
 * «Copri con un contratto» on the selected rows creates one (EstensioniSupporto).
 */

type Filtro = '' | 'expired' | 'expiring' | 'behind' | 'extended' | 'extended_expiring'

function stato(d: SoftwareDeadline) {
  if (d.status === 'extended' || d.status === 'extended_expiring') {
    return (
      <>
        <span className={`badge ${d.status === 'extended' ? 'badge--ok' : 'badge--warn'}`}>
          {d.status === 'extended' ? t('supporto esteso') : t('supporto esteso in scadenza')}
        </span>
        {d.extension && (
          <div className="text-muted eagle-piccolo">
            {d.extension.contract_id ? <Link to={`/contracts/${d.extension.contract_id}`}>{d.extension.ref}</Link> : d.extension.ref}
            {' · '}{t('fino al {d}', { d: dataBreve(d.extension.until) })}
          </div>
        )}
      </>
    )
  }
  if (d.status === 'expired') return <span className="badge badge--error">{t('fuori supporto')}</span>
  if (d.status === 'behind') return <span className="badge badge--warn">{t('non aggiornato')}</span>
  return <span className="badge badge--info">{t('in scadenza')}</span>
}

/** What an extension would name, from a row: product and cycle, or the system key. */
function partenzaDa(righe: SoftwareDeadline[]): Partenza | null {
  if (righe.length === 0) return null
  const chiave = (d: SoftwareDeadline) => {
    if (d.kind === 'system') return { kind: 'system' as const, product: d.system_key, cycle: '', label: d.product }
    const [, product = '', cycle = ''] = d.rule.split(':')
    return { kind: 'software' as const, product, cycle, label: d.product }
  }
  const prima = chiave(righe[0])
  const uguali = righe.every((d) => {
    const k = chiave(d)
    return k.kind === prima.kind && k.product === prima.product && k.cycle === prima.cycle && k.label === prima.label
  })
  if (!uguali) return null
  const ci = new Map(righe.map((d) => [d.ci_id, d.ci_name]))
  return { ...prima, ci: [...ci].map(([id, name]) => ({ id, name })) }
}

function quando(d: SoftwareDeadline): string {
  if (d.days === null) return '—'
  if (d.days < 0) return t('da {n} giorni', { n: (-d.days).toLocaleString(localeCorrente()) })
  return t('fra {n} giorni', { n: d.days.toLocaleString(localeCorrente()) })
}

export default function SoftwareEol() {
  const dati = useAsync(() => api.softwareDeadlines(), [])
  const { principal } = useAuth()
  const [filtro, setFiltro] = useState<Filtro>('')
  const [scelte, setScelte] = useState<Set<string>>(new Set())
  const [copri, setCopri] = useState<Partenza | null>(null)
  const righe = (dati.data?.items ?? []).filter((d) => !filtro || d.status === filtro)
  // Covering takes writing on the contracts: the operator's job.
  const puoCoprire = livelloModulo(principal, 'contracts') === 'write'
  const selezionate = (dati.data?.items ?? []).filter((d) => scelte.has(d.id) && d.status !== 'behind')
  const partenza = partenzaDa(selezionate)
  const colonne: Column<SoftwareDeadline>[] = [
    { key: 'status', header: t('Stato'), value: (d) => d.status, render: stato },
    { key: 'product', header: t('Prodotto') },
    { key: 'kind', header: t('Tipo'), value: (d) => (d.kind === 'system' ? t('Sistema operativo') : t('Software')) },
    { key: 'version', header: t('Versione'), mono: true },
    { key: 'cycle', header: t('Ciclo'), mono: true },
    { key: 'end_of_support', header: t('Fine supporto'), mono: true },
    { key: 'days', header: t('Quando'), value: (d) => d.days ?? 0, filter: false, render: quando },
    { key: 'ci_name', header: t('CI'), render: (d) => <Link to={`/cis/${d.ci_id}#sicurezza`}>{d.ci_name}</Link> },
  ]
  const c = dati.data?.counts
  const tile = (k: Filtro, valore: number, etichetta: string, tono: string) => (
    <button className={`stat stat--${valore ? tono : 'ok'}`} onClick={() => setFiltro(filtro === k ? '' : k)}>
      <div className="stat__value">{valore.toLocaleString(localeCorrente())}</div>
      <div className="stat__label">{etichetta}</div>
    </button>
  )
  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Scadenze dei software')}</h1>
          <p className="page__subtitle">
            {t('monitora il ciclo di vita di software e sistemi operativi installati, identificando i prodotti già fuori supporto, quelli a rilascio continuo non aggiornati e quelli che termineranno il supporto nei prossimi sei mesi. Ogni elemento viene registrato anche come rischio nella sezione Sicurezza del CI e aggiornato a ogni valutazione.')}
          </p>
        </div>
        <BottoniEsporta nome="censo-software-scadenze" disabled={!dati.data} path={api.softwareDeadlinesExportPath} />
      </header>
      {c && (
        <div className="stats">
          {tile('expired', c.expired, t('Fuori supporto'), 'error')}
          {tile('expiring', c.expiring, t('In scadenza entro 6 mesi'), 'warn')}
          {tile('behind', c.behind, t('Non aggiornati'), 'warn')}
          {(c.extended + c.extended_expiring > 0 || filtro === 'extended') && tile('extended', c.extended, t('Supporto esteso da contratto'), 'ok')}
          {(c.extended_expiring > 0 || filtro === 'extended_expiring') && tile('extended_expiring', c.extended_expiring, t('Supporto esteso in scadenza'), 'warn')}
        </div>
      )}
      {puoCoprire && scelte.size > 0 && (
        <div className="selezione-barra" role="region" aria-label={t('Righe selezionate')}>
          <span>{t('{n} selezionate', { n: scelte.size })}</span>
          <button type="button" disabled={!partenza} onClick={() => partenza && setCopri(partenza)}
                  title={partenza ? undefined : t('Scegli righe dello stesso prodotto e ciclo (o dello stesso sistema).')}>
            {t('Copri con un contratto')}
          </button>
          {!partenza && <span className="muted">{t('Scegli righe dello stesso prodotto e ciclo (o dello stesso sistema).')}</span>}
          <button type="button" className="secondary" onClick={() => setScelte(new Set())}>{t('Deseleziona')}</button>
        </div>
      )}
      {copri && (
        <ModuloEstensione partenza={copri} onAnnulla={() => setCopri(null)}
                          onFatto={async () => { setCopri(null); setScelte(new Set()); await dati.reload() }} />
      )}
      <Card>
        <ErrorBanner error={dati.error} />
        {dati.loading && !dati.data ? <Spinner /> : righe.length === 0 && !filtro ? (
          <EmptyState title={t('Nessuna scadenza')} hint={t('Le scadenze compaiono dopo una valutazione di sicurezza dei CI con l’inventario software.')} />
        ) : (
          <DataTable rows={righe} rowKey={(d) => d.id} columns={colonne} initialSort={{ key: 'days' }}
            conteggio={{ nome: t('scadenze') }} paginazione={{ predefinita: 50 }}
            selezione={puoCoprire ? { chiavi: scelte, onCambia: setScelte, id: (d) => d.id } : undefined} />
        )}
      </Card>
    </>
  )
}
